import React, { useState, useEffect, useMemo } from "react";
import { Box, Container, Skeleton, Stack, Chip, Typography } from "@mui/material";
import BreadcrumbsNav from "../../components/common/BreadCrumbNav";
import { get_service_list } from "../../api/ControllApi";
import ProfessionalFilterCard from "./ProfessinalFilterCard";
import ProfessionalServiceProduct from "./ProfessionalServiceProduct";
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem } from "@mui/material";
import { get_state, get_city, get_service_sub_categories, get_service_sub_sub_categories , get_proffessional_service } from "../../api/ControllApi";
import { useNavigate, useLocation } from "react-router-dom";
import { ASSET_BASE } from "../../utils/baseUrls";

export default function ProfessinalService() {
  const [loading, setLoading] = useState(true);
  const [serviceList, setServiceList] = useState([]);
  
  const [viewType, setViewType] = useState("grid");
  const navigate = useNavigate();
  const location = useLocation();

  // Derive filters and identifiers from URL query params
  const { filters, categoryNameQuery, categoryId } = useMemo(() => {
    const qp = new URLSearchParams(location.search);
    const catId = qp.get("category") || "";
    return {
      categoryId: catId,
      categoryNameQuery: qp.get("name"),
      filters: {
        category: catId,
        sub_category: qp.get("sub_category") || "",
        search: qp.get("search") || "",
        sub_sub_category: qp.get("sub_sub_category") ? [qp.get("sub_sub_category")] : [],
        brand_id: [], 
      }
    };
  }, [location.search]);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0
  });
  const [page, setPage] = useState(1);

  const [categories, setCategories] = useState([]);

  // Fetch Categories on Mount
  useEffect(() => {
    get_service_list()
      .then((res) => {
        const rawData = res?.data || res;
        const catData = rawData?.categories_list || rawData?.data?.categories_list || [];
        setCategories(Array.isArray(catData) ? catData : []);
      })
      .catch((err) => {
        console.error("Error fetching categories:", err);
      });
  }, []);

  // Compute selected category name
  const selectedCategoryName = useMemo(() => {
    if (categoryNameQuery && filters.category === categoryId) return categoryNameQuery;
    if (!filters.category) return "Professional Services";
    const found = categories.find(c => String(c.id) === String(filters.category));
    return found ? found.category_name : (categoryNameQuery || "Professional Services");
  }, [filters.category, categories, categoryNameQuery, categoryId]);


  const getServiceList = async (currentFilters = filters, currentPage = page) => {
    setLoading(true);
    try {
      const params = {
        category: currentFilters.category,
        sub_category: currentFilters.sub_category,
        search: currentFilters.search,
        page_no: currentPage,
      };

      if (currentFilters.sub_sub_category && currentFilters.sub_sub_category.length > 0) {
        params["sub_sub_category"] = currentFilters.sub_sub_category[0];
      }

      if (currentFilters.brand_id && currentFilters.brand_id.length > 0) {
        params["brand_id[]"] = currentFilters.brand_id;
      }

      const res = await get_service_list(params);
      
      // The API might return the list in res.data, res.data.data, or res.data.service
      const rawData = res?.data;
      const list = Array.isArray(rawData) ? rawData : (Array.isArray(rawData?.data) ? rawData.data : (Array.isArray(rawData?.services) ? rawData.services : []));
      
      setServiceList(list);
      
      // Update categories list from the response if available
      if (rawData?.categories_list) {
        setCategories(rawData.categories_list);
      }

      setPagination(res?.pagination || rawData?.pagination || { current_page: 1, last_page: 1, total: 0 });
    } catch (err) {

      setServiceList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getServiceList(filters, page);
  }, [filters, page]);

  const handleFilterChange = (newFilters) => {
    const params = new URLSearchParams();
    if (newFilters.category) params.set("category", newFilters.category);
    if (newFilters.sub_category) params.set("sub_category", newFilters.sub_category);
    if (newFilters.search) params.set("search", newFilters.search);
    if (newFilters.sub_sub_category && newFilters.sub_sub_category.length > 0) {
      params.set("sub_sub_category", newFilters.sub_sub_category[0]);
    }
    
    // Preserve name query if category remains the same
    if (categoryNameQuery && String(newFilters.category) === String(categoryId)) {
      params.set("name", categoryNameQuery);
    }
    
    navigate(`/professional-services?${params.toString()}`, { replace: true });
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleServiceSelect = (id, name) => {
    navigate(`/professional-services/services/${id}?service_name=${encodeURIComponent(name)}`);
  };

  return (
    <Container maxWidth={false} sx={{ width: { xs: '95%', md: '80%' }, mx: 'auto', py: 5 }}>
      <Box
        sx={{
          mb: 3,
          mt: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <BreadcrumbsNav />
          <Typography fontSize={{ xs: 16, sm: 18, md: 20 }}>
            Top{" "}
            <Box component="span" sx={{ color: "#95B160", fontWeight: 600 }}>
              {selectedCategoryName}
            </Box>{" "}
            Near By You
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "300px 1fr" },
          gap: 6,
        }}
      >
        <Box>
          <ProfessionalFilterCard
            filters={filters}
            onFilterChange={handleFilterChange}
            categoryName={categoryNameQuery}
          />
        </Box>
        <Box>
          {/* Active Filter Breadcrumbs (Optional: can be added back if needed) */}
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: viewType === "list" ? "1fr" : {
                xs: "1fr",
                sm: "1fr 1fr",
                lg: "1fr 1fr 1fr",
              },
            }}
          >
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={220} />
              ))
              : serviceList.length > 0
                ? serviceList.map((card) => (
                  <ProfessionalServiceProduct
                    key={card.id}
                    name={card.service_name || card.name}
                    image={(() => {
                      if (card.gallery) {
                        return card.gallery.split(",").map(img => 
                          img.startsWith("http") ? img : `${ASSET_BASE}${img}`
                        ).filter(img => !img.endsWith(".mp4"));
                      }
                      return Array.isArray(card.image) ? card.image : (card.image ? [card.image] : []);
                    })()}
                    description={card.description}
                    id={card.id}
                    selectionMode={true}
                    onSelect={(id) => handleServiceSelect(id, card.service_name || card.name)}
                  />
                ))
                : (
                  <Box
                    sx={{
                      gridColumn: "1 / -1",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "40vh",
                      textAlign: "center",
                      width: "100%",
                      py: 5,
                    }}
                  >
                    <Box
                      component="img"
                      src="/assets/images/nodatafound.png"
                      alt="No Data Found"
                      sx={{ width: { xs: 280, sm: 320, md: 350 }, mb: 2 }}
                    />
                  </Box>
                )}
          </Box>

          {/* PAGINATION */}
          {pagination.last_page > 1 && (
            <Stack direction="row" spacing={1} justifyContent="center" mt={10} mb={4}>
              {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(
                (p) => (
                  <Button
                    key={p}
                    variant={p === page ? "contained" : "outlined"}
                    onClick={() => handlePageChange(p)}
                    sx={{
                      borderRadius: "9px",
                      bgcolor: p === page ? "#FEB821" : "transparent",
                      color: p === page ? "#fff" : "#000",
                      borderColor: "#FEB821",
                      minWidth: 40,
                      height: 40,
                      "&:hover": {
                        bgcolor: p === page ? "#FEB821" : "#F3CF7A",
                        borderColor: "#FEB821",
                      },
                    }}
                  >
                    {p}
                  </Button>
                )
              )}
            </Stack>
          )}
        </Box>
      </Box>
    </Container>
  );
}